
/**
 * Module dependencies.
 */

import { ifProp, theme } from 'styled-tools';
import Image from 'src/components/core/image';
import React, { ReactElement } from 'react';
import map from 'lodash/map';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  activeImage?: string | null,
  className?: string,
  image?: string | null,
  images: string[]
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  overflow: hidden;
  position: relative;
`;

/**
 * `Layer` styled component.
 *
 * A wrapper rather than a styled `Image`, which would forward `isActive` onto
 * the `img` element.
 */

const Layer = styled.div<{ isActive: boolean }>`
  bottom: 0;
  left: 0;
  opacity: ${ifProp('isActive', 1, 0)};
  position: absolute;
  right: 0;
  top: 0;
  transition: opacity ${theme('animations.defaultTransition')};
`;

/**
 * `MenuImage` component.
 *
 * Takes the place of the `MenuCard` in a mega menu whose links have images: a
 * plain image block, with no link, title or arrow, that shows the image of the
 * hovered or focused link and falls back to `image` otherwise.
 *
 * Every image is rendered up front, hidden, so swapping between them does not
 * wait for a download — hence the `sizes`, the block takes about half the
 * viewport and they would otherwise load at full width.
 */

const MenuImage = ({ activeImage, className, image, images }: Props): ReactElement => (
  <Wrapper className={className}>
    {image && (
      <Image
        alt={''}
        layout={'fill'}
        objectFit={'cover'}
        sizes={'50vw'}
        src={image}
      />
    )}

    {map(images, linkImage => (
      <Layer
        isActive={linkImage === activeImage}
        key={linkImage}
      >
        <Image
          alt={''}
          layout={'fill'}
          objectFit={'cover'}
          sizes={'50vw'}
          src={linkImage}
        />
      </Layer>
    ))}
  </Wrapper>
);

/**
 * Export `MenuImage` component.
 */

export default MenuImage;
